import { useState } from 'react';
import PhoneAuth from './phoneauth';
import OTPAuth from './otpauth';
import RegisterForm from './registerauth';
import SideContent from './sidecontent';
import { useAuth } from '../../providers/auth.provider';

// Define LoginState type
type LoginState = 'PHONE' | 'OTP' | 'PASSWORD' | 'REGISTER';

const LoginStates: Record<string, LoginState> = {
  PHONE: 'PHONE',
  OTP: 'OTP',
  PASSWORD: 'PASSWORD',
  REGISTER: 'REGISTER'
};

export default function Login() {
  // State variables
  const [loginState, setLoginState] = useState(LoginStates.PHONE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [authToken, setAuthToken] = useState('');
  const { partnerData } = useAuth();
  
  const companyName = partnerData?.company_name || 'LARKx';

  // Handle password login
  const handlePasswordLogin = () => {
    if (phoneNumber.length >= 10) {
      setLoginState(LoginStates.PASSWORD);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center justify-center p-4">
      <p className="text-center font-bold text-4xl mb-2">
        Welcome to <span className="text-primary-950">{companyName}</span>
      </p>
      <p className="text-lg font-semibold text-center text-gray-700 mb-5">
        Financial solutions for your future
      </p>

      <div className="w-full max-w-7xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left side content - hidden on mobile */}
        <div className="hidden md:flex flex-col space-y-8 p-8">
          <SideContent />
        </div>

        {/* Right side with login form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white shadow-xl rounded-lg p-6 space-y-4 border border-gray-100">
            <div>
              <h1 className="text-xl font-medium text-gray-900">Get Started</h1>
              <p className="text-gray-600 mt-2">Access your account securely</p>
            </div>

            {/* Conditional rendering based on login state */}
            {loginState === LoginStates.PHONE && (
              <>
                <PhoneAuth
                  setLoginState={setLoginState}
                  onPasswordLogin={handlePasswordLogin}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                />
                
                {/* Consent text statement */}
                <div className="mt-4 text-xs text-gray-600">
                  <p className="mb-2">By proceeding, I accept {companyName}'s <a href="https://www.larkfinserv.com/legal/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a> and <a href="https://www.larkfinserv.com/legal/terms-and-conditions" className="text-blue-600 hover:underline">Terms of Use</a>.</p>
                  
                  <p className="mb-2">I authorize {companyName}, affiliates and/or business associates and their respective representatives to send me its/their promotional communications regarding their respective products and/or services by telephone calls / SMSs / emails / WhatsApp or any other electronic modes.</p>
                  
                  <p className="mb-2">I authorize {companyName} to share my personal data with lending partners to process my loan application.</p>
                  
                  <p className="mb-2">I authorize {companyName} to share my personal data with third-party partners to provide me Products/Services of the partner. <span className="text-gray-500 italic">This is applicable only for loan-level data.</span></p>
                </div>
              </>
            )}

            {loginState === LoginStates.OTP && (
              <OTPAuth
                setAuthToken={setAuthToken}
                setLoginState={setLoginState}
                phoneNumber={phoneNumber}
                onBack={() => setLoginState(LoginStates.PHONE)}
              />
            )}

            {loginState === LoginStates.REGISTER && (
              <RegisterForm authToken={authToken} />
            )}
          </div>
        </div>
      </div>

      {/* Footer logo */}
      {partnerData && (
        <div className="mt-6 flex items-center gap-2">
          powered by 
          <span className="font-semibold text-blue-800">
            <img src="/assets/images/logo.png" alt="Logo" className="max-w-20" />
          </span>
        </div>
      )}
    </div>
  );
}